import moment from "moment";
import React from "react";
import AnimatedNumber from "../components/AnimatedNumber";
import BonusBoard from "../components/BonusBoard";
import ContentSidebar from "../components/ContentSidebar";
import GameBoard from "../components/GameBoard";
import KenoLogo from "../components/KenoLogo";
import LoadingIndicator from "../components/LoadingIndicator";
import TicketsSidebar from "../components/TicketsSidebar";
import { BASE_10 } from "../constants";
import ICustomer from "../interfaces/ICustomer";
import IKenoDraw from "../interfaces/IKenoDraw";
import IProductPlayType from "../interfaces/IProductPlayType";
import ITicket from "../interfaces/ITicket";
import {
    BONUS_PHASE_SECONDS,
    DRAW_NUMBER_SECONDS,
    HIGHLIGHT_NUMBER_DELAY_SECONDS,
    NUMBERS_TO_DRAW,
    OLD_DRAWS_LIMIT,
} from "../settings";
import convertSelectionsToNumbers from "../utils/convertSelectionsToNumbers";
import filterNumberSelections from "../utils/filterNumberSelections";
import formatCountdown from "../utils/formatCountdown";
import history from "../utils/history";
import OGSAPI from "../utils/OGSAPI";

interface IProps {
    currentDraw?: IKenoDraw;
    customer?: ICustomer;
    fetchedCustomer: boolean;
    nextDraw: IKenoDraw;
    pastDraws: {
        [key: string]: IKenoDraw | undefined;
    };
    productPlayType: IProductPlayType;
}

const SECOND_IN_MS = 1000;

enum AnimationPhase {
    done = "DONE",
    drawing = "DRAWING",
    bonus = "BONUS",
}

interface IState {
    animateNumber: number | undefined;
    animationPhase: AnimationPhase;
    drawnNumbers: number[];
    fetchedTickets: boolean;
    initialTicketsRequested: boolean;
    selectedTicket: ITicket | undefined;
    tickets: ITicket[];
    visibleDrawnNumbers: number[];
}

export default class DrawPage extends React.Component<IProps, IState> {
    public secondsRemaining: number = 0;
    public state: IState = {
        animateNumber: undefined,
        animationPhase: AnimationPhase.done,
        drawnNumbers: [],
        fetchedTickets: false,
        initialTicketsRequested: false,
        selectedTicket: undefined,
        tickets: [],
        visibleDrawnNumbers: [],
    };
    public componentDidMount(): void {
        this.componentWillReceiveProps(this.props);
    }
    public componentWillReceiveProps(nextProps: IProps): void {
        const { currentDraw, customer } = nextProps;

        if (this.props.currentDraw !== currentDraw) {
            this.setState({
                visibleDrawnNumbers: [],
            });
        }

        if (customer && currentDraw && !this.state.initialTicketsRequested) {
            this.setState({
                initialTicketsRequested: true,
            });
            this.refreshTickets(currentDraw);
        }

        const DRAW_NUMBERS_SECONDS = DRAW_NUMBER_SECONDS * NUMBERS_TO_DRAW;

        let scrubPosition: number = 0;
        if (currentDraw && currentDraw.drawTimestamp) {
            scrubPosition = moment().unix() - currentDraw.drawTimestamp;
        }

        const BONUS_PHASE_START = 0;
        const BONUS_PHASE_END = BONUS_PHASE_START + BONUS_PHASE_SECONDS;

        const DRAW_PHASE_START = BONUS_PHASE_END;
        const DRAW_PHASE_END = DRAW_PHASE_START + DRAW_NUMBERS_SECONDS;

        const DONE_PHASE_START = DRAW_PHASE_END;

        let drawnNumbers: number[] = [];
        let animationPhase: AnimationPhase = AnimationPhase.bonus;

        // TODO: Show a bonus round animation
        if (scrubPosition >= BONUS_PHASE_START && scrubPosition < BONUS_PHASE_END) {
            animationPhase = AnimationPhase.bonus;

        // TODO: Show an animation of numbers highlighting on the board
        } else if (scrubPosition >= DRAW_PHASE_START && scrubPosition < DRAW_PHASE_END) {
            animationPhase = AnimationPhase.drawing;

        // TODO: Complete the round and wait until next round
        } else if (scrubPosition >= DONE_PHASE_START) {
            animationPhase = AnimationPhase.done;
            this.setState({
                animateNumber: undefined,
            });
        }

        if (currentDraw && currentDraw.results && currentDraw.results.main) {
            const availableNumbers = currentDraw.results.main[0] || [];
            if (animationPhase === AnimationPhase.drawing) {

                // Slice which numbers have progressed so far
                const phaseProgress = scrubPosition - DRAW_PHASE_START;
                const drawnNumbersIndex = Math.floor(phaseProgress / DRAW_NUMBER_SECONDS);
                drawnNumbers = availableNumbers.slice(0, drawnNumbersIndex + 1);

                // If entering mid-draw, display current number position
                const enteringMidDraw = !this.props.currentDraw;
                if (enteringMidDraw) {
                    this.setState({
                        visibleDrawnNumbers: drawnNumbers,
                    });

                // Otherwise, animate the most recent number
                } else {
                    // Show some animation for numbers
                    const existingDrawnNumbers = this.state.drawnNumbers;
                    const newNumbers = drawnNumbers.filter((num: number): boolean => {
                        return existingDrawnNumbers.includes(num);
                    });

                    // Animate the newest number drawn
                    if (newNumbers.length) {
                        const animateNumber = newNumbers[newNumbers.length - 1];
                        this.setState({
                            animateNumber,
                        });

                        // After a delay, visibly highlight the number
                        setTimeout((): void => {
                            this.setState({
                                visibleDrawnNumbers: drawnNumbers,
                            });

                        }, HIGHLIGHT_NUMBER_DELAY_SECONDS * SECOND_IN_MS);
                    }
                }

            } else if (animationPhase === AnimationPhase.done) {
                this.setState({
                    visibleDrawnNumbers: availableNumbers,
                });
            }
        }

        this.setState({
            animationPhase,
            drawnNumbers,
        });
    }
    public render(): JSX.Element {
        const {
            animateNumber,
            animationPhase,
            tickets,
            fetchedTickets,
            selectedTicket,
            visibleDrawnNumbers,
        } = this.state;
        const {
            currentDraw,
            customer,
            fetchedCustomer,
            nextDraw,
            pastDraws,
            productPlayType,
        } = this.props;

        const selections = (selectedTicket && selectedTicket.selections) ?
            filterNumberSelections(selectedTicket.selections) : [];
        const numberSelections = filterNumberSelections(selections);

        let selectedNumbers: number[] = [];
        if (
            currentDraw &&
            selectedTicket &&
            selectedTicket.event &&
            selectedTicket.event.externalId === currentDraw.id
        ) {
            selectedNumbers = convertSelectionsToNumbers(numberSelections);
        }

        let drawBonus = "-";
        let drawId = "-";
        let bonus: number | undefined;
        if (currentDraw) {
            if (currentDraw.results && currentDraw.results.bonus) {
                bonus = currentDraw.results.bonus[0][0];
                drawBonus = `${ bonus }x`;
            }
            drawId = currentDraw.id;
        }

        let countdown = "-";
        if (nextDraw && nextDraw.scheduledDrawTimestamp) {
            countdown = formatCountdown(nextDraw.scheduledDrawTimestamp, {
                zero: "0:00",
            });
        }

        const prizeTable = productPlayType.settings.prizeTable;

        const loadingSidebar = fetchedCustomer && customer && !fetchedTickets;

        const filteredTickets = this.filterNewTickets(tickets);

        return (
            <div className="draw-page columns is-gapless">
                <div className="sidebar column is-4">
                    { loadingSidebar ? (
                        <div className="whole-screen-loading">
                            <LoadingIndicator />
                        </div>
                    ) : (
                        filteredTickets.length ? (
                            <div>
                                <div className="sidebar-wrapper">
                                    <div className="sidebar-content">
                                        <button
                                            className="styled-button"
                                            type="button"
                                            onClick={ (): void => { history.push("/purchase/"); } }
                                        >Buy Another Ticket</button>
                                    </div>
                                </div>
                                <div className="scrollable-tickets">
                                    { currentDraw ? (
                                        <TicketsSidebar
                                            currentDraw={ currentDraw }
                                            drawnNumbers={ visibleDrawnNumbers }
                                            selectedTicket={ selectedTicket }
                                            onSelectTicket={ this.selectTicket.bind(this) }
                                            tickets={ filteredTickets }
                                            prizeTable={ prizeTable }
                                            pastDraws={ pastDraws }
                                        />
                                    ) : undefined }
                                </div>
                            </div>
                        ) : (
                            <div className="sidebar-wrapper">
                                <div className="sidebar-content">
                                    <ContentSidebar />
                                </div>
                            </div>
                        )
                    ) }
                </div>
                <div className="play-area column is-8">
                    <div className="play-area-wrapper">
                        <div className="play-area-header">
                            { animationPhase === AnimationPhase.done ? (
                                "Draw Complete"
                            ) : (
                                "Draw In Progress..."
                            ) }
                        </div>
                        { animationPhase === AnimationPhase.bonus ? (
                            <BonusBoard
                                bonus={ bonus }
                            />
                        ) : (
                            <>
                                { animateNumber ? (
                                    <AnimatedNumber
                                        key={ animateNumber }
                                        value={ animateNumber }
                                        selected={ selectedNumbers.includes(animateNumber) }
                                    />
                                ) : undefined }
                                <GameBoard
                                    mode="draw"
                                    highlightOddEven={ animationPhase === AnimationPhase.done }
                                    drawnNumbers={ visibleDrawnNumbers }
                                    currentDraw={ currentDraw }
                                    nextDraw={ nextDraw }
                                    selectedNumbers={ selectedNumbers }
                                >
                                    <div className="columns is-mobile">
                                        <div className="column">
                                            <KenoLogo />
                                        </div>
                                        <div className="column">
                                            <label>Current Draw</label>
                                            <var>{ drawId }</var>
                                        </div>
                                        <div className="column">
                                            <label>Bonus</label>
                                            <var>{ drawBonus }</var>
                                        </div>
                                        <div className="column">
                                            <label>Next Draw</label>
                                            <var>{ countdown }</var>
                                        </div>
                                    </div>
                                </GameBoard>
                            </>
                        ) }
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Refresh the tickets for the currentDraw and previous X draws.
     *
     * @param currentDraw Keno draw to use as a basis for current
     */
    protected refreshTickets(currentDraw: IKenoDraw): Promise<ITicket[]> {
        const currentDrawId = parseInt(currentDraw.id, BASE_10);
        const startDrawId = Math.max(1, currentDrawId - OLD_DRAWS_LIMIT);

        const promise = OGSAPI.fetchTickets({
            event__externalId__gte: startDrawId,
            includeSelections: true,
        });

        promise.then((tickets: ITicket[]) => {
            this.setState({
                fetchedTickets: true,
                tickets,
            });
        });

        return promise;
    }
    protected selectTicket(selectedTicket: ITicket): void {
        this.setState({
            selectedTicket,
        });
    }
    private filterNewTickets(tickets: ITicket[]): ITicket[] {
        const { currentDraw } = this.props;
        if (!currentDraw) {
            return [];
        }
        const currentDrawId = parseInt(currentDraw.id, BASE_10);
        return tickets.filter((ticket: ITicket): boolean => {
            if (!ticket.event) {
                return false;
            }
            return parseInt(ticket.event.externalId, BASE_10) >= (currentDrawId - OLD_DRAWS_LIMIT);
        });
    }
}



// WEBPACK FOOTER //
// ./src/pages/DrawPage.tsx